import { A } from '@ember/array';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isBlank, isPresent, isNone } from '@ember/utils';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { addMinutes, endOfDay, startOfDay, subDays, addDays } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';
import type AbilitiesService from 'ember-can/services/abilities';
import type { EncapsulatedTask, TaskInstance } from 'ember-concurrency';
import { timeout, task, dropTask, restartableTask, enqueueTask } from 'ember-concurrency';
import config from 'garaje/config/environment';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type Employee from 'garaje/models/employee';
import type EmployeeVisitMetrics from 'garaje/models/employee-visit-metrics';
import type Entry from 'garaje/models/entry';
import type EventReport from 'garaje/models/event-report';
import type Flow from 'garaje/models/flow';
import type Invite from 'garaje/models/invite';
import type { Field } from 'garaje/models/location';
import type SignInFieldPage from 'garaje/models/sign-in-field-page';
import type WorkplaceDay from 'garaje/models/workplace-day';
import type AsyncExportManagerService from 'garaje/services/async-export-manager';
import type CurrentAdminService from 'garaje/services/current-admin';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LoggerService from 'garaje/services/logger';
import type MessageBusService from 'garaje/services/message-bus';
import type MetricsService from 'garaje/services/metrics';
import type SessionService from 'garaje/services/session';
import type StateService from 'garaje/services/state';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { fetchEmployeeVisitMetrics } from 'garaje/utils/employee-visit-metrics';
import employeesSearcherTask from 'garaje/utils/employees-searcher';
import type { EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import { DEFAULT_FLOW_NAME, FlowType, NON_ASSIGNABLE_FLOWS } from 'garaje/utils/enums';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';
import _uniqBy from 'lodash/uniqBy';
import { alias, and, equal, notEmpty } from 'macro-decorators';
import moment from 'moment-timezone';
import { all, resolve, defer } from 'rsvp';

import type { WorkplaceEntriesModel } from './route';

interface FilterOption {
  name: string;
  filter: string;
  employee?: Employee;
}

interface EntryFilter {
  location: string;
  visitor_type: string;
  query?: string;
  'start-date'?: string;
  'end-date'?: string;
  status?: string;
  employee?: string;
}

interface EntryParams {
  include: string;
  filter: EntryFilter;
  sort: string;
  page: { offset: number; limit: number };
}

type SortableField = Field & {
  sort?: string;
};

const HEADER_ENUMS = {
  signedInHeader: 'Signed In',
  signedOutHeader: 'Reservation End Time',
  reservationStartTimeHeader: 'Reservation Start Time',
  name: 'Name',
  employeeRegistration: 'Employee Registration',
  expectedArrivalTime: 'Expected Arrival Time',
  date: 'Date',
};

const EVENT_REPORT_POLLING_TIMEOUT = zft(10000);
const NUMBER_OF_POLLING_TRIES = zft(3);
const PRIMARY_HEADER = [
  { name: HEADER_ENUMS.name, componentName: 'custom-column', sort: 'full_name' },
  { name: 'Entry status', componentName: 'custom-column' },
  { name: HEADER_ENUMS.employeeRegistration, componentName: 'employee-registration-column' },
  { name: 'Pre-registration', componentName: 'pre-registration-column' },
  { name: 'Desk', componentName: 'custom-column' },
  { name: 'Security', componentName: 'plugin-activity-column' },
  { name: HEADER_ENUMS.reservationStartTimeHeader, componentName: 'reservation-start-time-column' },
  { name: HEADER_ENUMS.date, componentName: 'entry-date-column' },
];

const SIGN_IN_AND_SIGN_OUT_HEADER = [
  { name: HEADER_ENUMS.signedInHeader, componentName: 'signed-in-column', sort: 'signed-in-at' },
  { name: HEADER_ENUMS.signedOutHeader, componentName: 'signed-out-column', sort: 'sign_out_time' },
];

const EXPECTED_ARRIVAL_TIME_HEADER = [
  {
    name: HEADER_ENUMS.expectedArrivalTime,
    componentName: 'expected-arrival-time-column',
  },
];

const DAYS_OF_WEEK_ARRAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type EntryOrInvite = Entry | Invite;

export default class WorkplaceEntriesController extends Controller {
  declare model: WorkplaceEntriesModel;

  @service declare abilities: AbilitiesService;
  @service declare asyncExportManager: AsyncExportManagerService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare currentLocation: CurrentLocationService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare logger: LoggerService;
  @service declare messageBus: MessageBusService;
  @service declare metrics: MetricsService;
  @service declare session: SessionService;
  @service declare state: StateService;
  @service declare store: Store;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  queryParams = ['date', 'query', 'filter', 'refresh', 'sort', { selectedFlow: { as: 'visitor_type' } }];

  @tracked refresh = ''; // Flag to force a model refresh
  @tracked date = '';
  @tracked query: string | null = '';
  @tracked inputQuery: string | null = null;
  @tracked selectedFlow: string = DEFAULT_FLOW_NAME.EMPLOYEE_SCREENING;
  @tracked entriesCount = 0;
  @tracked sort = '-signed-in-at';
  @tracked invitesSort = 'expected_arrival_time';
  @tracked filter = 'all';
  @tracked showExportModal = false;
  @tracked locationsCapacity: EmployeeVisitMetrics | null = null;
  @tracked startDate: Date | string | null = null;
  @tracked endDate: Date | string | null = null;

  get displayedMetrics(): { scheduled: number; signedIn: number; signedOut: number; total: number } | null {
    // When searching, calculate metrics from loaded entries (already filtered by search + status)
    if (isPresent(this.query)) {
      const scheduled = this.noShowInvites.length;
      const signedIn = this.signedInEntries.length;
      const signedOut = this.signedOutEntries.length;

      // When a status filter is active, show only that status count
      const statusFilterMap = {
        'signed-in': { total: signedIn, scheduled: 0, signedIn, signedOut: 0 },
        'signed-out': { total: signedOut, scheduled: 0, signedIn: 0, signedOut },
        'no-show': { total: scheduled, scheduled, signedIn: 0, signedOut: 0 },
      };

      const filteredMetrics = statusFilterMap[this.filter as keyof typeof statusFilterMap];
      if (filteredMetrics) {
        return filteredMetrics;
      }

      // No status filter - return all counts
      return {
        scheduled,
        signedIn,
        signedOut,
        total: scheduled + signedIn + signedOut,
      };
    }
    // Otherwise use API metrics
    return this.locationsCapacity;
  }

  get shouldShowMetrics(): boolean {
    return this.displayedMetrics !== null;
  }

  @tracked mobileCalendarVisible = false;
  @tracked page = 1;
  @tracked employees: Employee[] = [];
  @tracked employeesPage = 0;
  @tracked totalLoadedEntries!: number;
  @tracked totalLoadedInvites!: number;
  @tracked hasEntries?: boolean;
  @tracked selectedEntries: EntryOrInvite[] = [];
  @tracked eventReports: EventReport[] = [];
  @tracked datesWithEntries = [];
  @tracked bosses: Employee[] = [];
  @tracked showCalendar = false;
  @tracked selectedDateRange = 'Today';

  @tracked entriesAndInvites: EntryOrInvite[] = [];

  @tracked employeesCount = 0;
  @tracked limit = 25;
  dateRangeFilterOptions = [
    'Today',
    'Yesterday',
    'Tomorrow',
    'Past 7 days',
    'Past 14 days',
    'Past 30 days',
    'Next 7 days',
    'Next 14 days',
    'Next 30 days',
    'Custom range',
  ];

  constructor(properties: Record<string, unknown>) {
    super(properties);

    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  @alias('currentLocation.preRegistrationEnabled') preRegistrationEnabled!: boolean;
  @equal('hasEntries', false) noHistoricVisitors!: boolean;
  @equal('dateWithDefault', moment().format('YYYY-MM-DD')) isToday!: boolean;
  @notEmpty('query') showEntryFullDate!: boolean;
  @notEmpty('query') showClearButton!: boolean;
  @and('page', 'loadEntries.isIdle', 'mutateQuery.isIdle') showCount!: boolean;

  get currentLocationId(): string {
    return this.currentLocation.location.id;
  }

  get isAfterToday(): boolean {
    return moment(this.dateWithDefault).isAfter(moment(), 'day');
  }

  get selectedDateRangeOption(): string {
    return this.isToday ? 'Today' : `Current day (${this.selectedDate.format('MMM D, YYYY')})`;
  }

  get currentQuery(): string | null {
    // If a search query typed in, use it.
    // Fallback to query param.
    const { inputQuery, query } = this;

    return isNone(inputQuery) ? query : inputQuery;
  }

  get isSearching(): boolean {
    return this.filter !== 'all' || isPresent(this.query);
  }

  get exportButtonText(): string {
    return this.isSearching ? 'Export results' : 'Export';
  }

  get exportIframeUrl(): string {
    const { currentCompany } = this.state;
    const locationId = this.currentLocation.location.id;
    let modalLabel;
    let selectedFlowId;
    const defaultEndDate = moment().format('YYYY-MM-DD');
    const defaultStartDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
    const flows = this.model.flows;
    if (this.selectedFlow === '') {
      selectedFlowId = this.model.locationFlowIds.join(',');
      modalLabel = 'All employees types';
    } else {
      if (this.selectedFlow === DEFAULT_FLOW_NAME.EMPLOYEE_SCREENING) {
        selectedFlowId = flows.find((flow) => flow.type === FlowType.EMPLOYEE_SCREENING)?.id;
      } else {
        selectedFlowId = flows.find((flow) => flow.name === this.selectedFlow)?.id;
      }
      modalLabel = this.selectedFlow;
    }
    return urlBuilder.embeddedInviteExportModalUrl(
      currentCompany.id,
      locationId,
      selectedFlowId,
      defaultStartDate,
      defaultEndDate,
      modalLabel,
    );
  }

  /*
  A computed property that provides a fallback to today's date if the date QP is empty.

  This allows users to bookmark a live "today" view that is just `/entries`, since the empty QP
  will force the app to show the current day's view rather than forcing the URL to always contain
  a static date.
   */
  get dateWithDefault(): string {
    return this.date || moment().format('YYYY-MM-DD');
  }

  /*
   Instance of "moment" representing the string in the property date.
   This value is used to render the envoy-calendar, and do operations
   like next and previous day.
   */
  get selectedDate(): moment.Moment {
    return moment(this.dateWithDefault, 'YYYY-MM-DD');
  }

  set selectedDate(value: moment.Moment) {
    this.date = value.format('YYYY-MM-DD');
    void this.loadEntries.cancelAll();
    // @ts-ignore
    // eslint-disable-next-line no-setter-return
    return value;
  }

  @action
  didSelectDateRange(startDate: Date, endDate: Date): void {
    this.workplaceMetrics.trackEvent('EMPLOYEE_LOG_DATE_RANGE_SELECT_CLICKED');

    this.startDate = startDate;
    this.endDate = endDate;
    this.selectedDateRange = 'Custom range';
    void this.loadEntriesRestartableTask.perform();
  }

  get startDateFormat(): string {
    const timeZone = this.state.currentLocation.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const zonedTime = toZonedTime(new Date(this.startDate!), timeZone);
    return format(new Date(zonedTime), 'MM/dd/yyyy', { timeZone: timeZone });
  }

  get endDateFormat(): string {
    const timeZone = this.state.currentLocation.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const zonedTime = toZonedTime(new Date(this.endDate!), timeZone);
    return format(zonedTime, 'MM/dd/yyyy', { timeZone: timeZone });
  }

  @action
  setStartDate(date: Date): void {
    this.startDate = date;
  }

  @action
  setEndDate(date: Date): void {
    this.endDate = date;
  }

  @action
  setStartAndEndTime(): void {
    this.startDate = addMinutes(startOfDay(new Date()), this.state.minutesBetweenTimezones());
    this.endDate = addMinutes(endOfDay(new Date()), this.state.minutesBetweenTimezones());
  }

  get showSignedOut(): boolean {
    return this.filter !== 'signed-in';
  }

  /*
    We should only show signed in and signed out for employees who
    have shown up.
  */
  get showSignedInAndOutHeaders(): boolean {
    return this.filter !== 'no-show';
  }

  get showExpectedArrivalTimeHeader(): boolean {
    return this.filter == 'no-show' || this.filter == 'all';
  }

  get filterOptions(): FilterOption[] {
    const options = [];
    const employee = this.currentAdmin?.employee;
    if (employee) {
      this.bosses.forEach((boss) => {
        options.push({
          name: `${boss.name} Employees`,
          filter: boss.id,
          employee: boss,
        });
      });
    }
    // Only include all employees filter if user is an admin or if is
    // an employee with bosses
    let canViewAllReservations = false;
    if (this.featureFlags.isEnabled('workplace-permission-manage-employee-log')) {
      canViewAllReservations = this.abilities.can('view employee invite in workplace');
    } else {
      canViewAllReservations = this.currentAdmin?.isAdminLike;
    }
    if (canViewAllReservations || this.bosses?.length > 0) {
      options.unshift({ name: 'All Reservations', filter: 'all' });
    }
    options.push(
      { name: 'Signed In', filter: 'signed-in' },
      { name: 'Signed Out', filter: 'signed-out' },
      { name: 'Scheduled Employees', filter: 'no-show' },
    );
    return options;
  }

  get selected(): FilterOption | undefined {
    const filter = this.filter;
    const filterOptions = this.filterOptions;
    const selectedOption = filterOptions.find((option) => option.filter === filter);
    return selectedOption || filterOptions[0];
  }

  get signedInEntries(): EntryOrInvite[] {
    return this.entriesAndInvites.filter((entry) => 'signInTime' in entry && entry.signInTime);
  }

  get signedOutEntries(): EntryOrInvite[] {
    return this.entriesAndInvites.filter((entry) => 'signOutTime' in entry && entry.signOutTime);
  }

  get noShowInvites(): EntryOrInvite[] {
    return this.entriesAndInvites.filter((entry) => 'arrived' in entry && !entry.arrived);
  }

  get allInvitesAndEntries(): EntryOrInvite[] {
    return this.entriesAndInvites.filter((entry) => !('arrived' in entry && entry.arrived));
  }

  get relevantEntries(): EntryOrInvite[] {
    switch (this.filter) {
      case 'all':
        return this.allInvitesAndEntries;
      case 'no-show':
        return this.noShowInvites;
      case 'signed-in':
        return this.signedInEntries;
      default:
        return this.signedOutEntries;
    }
  }

  get relevantReviewableEntries(): EntryOrInvite[] {
    return this.relevantEntries.filter((entry) => {
      return (
        entry.needsApprovalReview &&
        this.abilities.can('review entry-approval', {
          context: 'location',
          visitApproval: entry.visitApproval,
          model: entry,
        })
      );
    });
  }

  get hasMorePages(): boolean {
    const currentTotal = this.page * this.limit;
    return currentTotal <= this.entriesCount;
  }

  get signInFieldPages(): SignInFieldPage[] {
    return this.model.signInFieldPages;
  }

  get customFieldHeaders(): Field[] {
    return _uniqBy(this.model.customFields.flat(), 'name').map((customField) => {
      return {
        name: customField.label,
        componentName: 'custom-column',
      };
    });
  }

  get primaryHeaders(): Field[] {
    const headers = PRIMARY_HEADER;
    const removeHeaders: Record<string, boolean> = {
      Notification: !this.currentLocation.location.hostNotificationsEnabled,
      Security: !this.hasSecurityColumn,
      'Pre-registration': !this.currentLocation.location.preRegistrationEnabled,
      Desks: this.abilities.can('see entry log for desk'),
    };
    return headers.filter(({ name }) => !removeHeaders[name]);
  }

  get hasSecurityColumn(): boolean {
    return this._hasActivePluginForCategory('security');
  }

  get entryDashboardFields(): Field[] {
    if (this.selectedFlow.toLowerCase() != 'employee registration') {
      return this.currentLocation.location.dashboardFields;
    }
    const dashboardFields = this.currentLocation.location.dashboardFields;
    return [
      ...dashboardFields.slice(),
      { name: 'Employee Registration', componentName: 'employee-registration-column' },
    ];
  }

  get fieldOptions(): Field[] {
    // this CP transform raw dashboardFields into list of headers and their property
    // entry headers always displayed in the same order (which defined here)
    // Note: field has to be populated when sortable is true
    // In addition, column-name HAS TO be specify because backend is checking it too
    const primaryHeaders = this.primaryHeaders;
    const customFieldHeaders = this.customFieldHeaders;
    // order has to be primary > custom > sign in and sign out
    let headers: SortableField[] = [
      ...primaryHeaders,
      ...customFieldHeaders,
      ...(this.showSignedInAndOutHeaders ? SIGN_IN_AND_SIGN_OUT_HEADER : []),
      ...(this.showExpectedArrivalTimeHeader ? EXPECTED_ARRIVAL_TIME_HEADER : []),
    ];
    headers = headers.map(({ name, componentName, sort }) => {
      return {
        name,
        componentName,
        // always show name
        show: name === 'Name' ? true : isPresent(this.entryDashboardFields.find((field) => field.name === name)),
        // name should not be adding / removing from dashboard fields
        disabled:
          name === 'Name'
            ? true
            : (() =>
                this.selectedFlow.toLowerCase() === 'employee registration' &&
                name.toLowerCase() === 'employee registration')(),
        sort,
      };
    });
    return headers;
  }

  get flowOptions(): Array<Partial<Flow>> {
    const options = [{ name: 'All Employees' }];
    if (this.currentLocation.location.employeeScreeningEnabled) {
      options.push({ name: DEFAULT_FLOW_NAME.EMPLOYEE_SCREENING });
    }

    this.currentLocation.location.flows?.content
      ?.filter(({ employeeCentric, type, name }) => !NON_ASSIGNABLE_FLOWS.includes(type) && employeeCentric && name)
      .toSorted((a, b) => a.name.localeCompare(b.name))
      .forEach((flow) => options.push(flow));
    return _uniqBy(options.filter(Boolean), 'name');
  }

  get modalElement(): HTMLElement | null {
    return document.getElementById('modal');
  }

  get canUsePartialDay(): boolean {
    return this.abilities.can('use partial day booking desk');
  }

  selectedDidChange(selected: Entry[]): void {
    this.selectedEntries = selected;
  }

  selectAllEntries(): void {
    this.selectedEntries = [...this.relevantEntries];
  }

  @action
  toggleModal(): void {
    this.showExportModal = !this.showExportModal;
    this.workplaceMetrics.trackEvent('WORKPLACE_EMPLOYEE_LOG_EXPORT_MODAL_BUTTON_CLICKED');
  }

  @action
  exportOrShowModal(): void {
    if (this.isSearching) {
      void this.exportEntries(false);
    } else {
      const date = this.selectedDate.clone();
      this.startDate = date.startOf('day').format();
      this.endDate = date.endOf('day').format();
      this.showExportModal = true;
    }
  }

  @action
  exportEntries(isExportLink = false): TaskInstance<void> {
    let params = this.asyncEntryExportParams();

    this.workplaceMetrics.trackEvent('WORKPLACE_EMPLOYEES_LOG_EXPORT_BUTTON_CLICKED', params);
    this.showExportModal = false;
    if (isExportLink) {
      params = { ...params, action_origin: 'export_link' };
    } else {
      if (params['filter'] == 'all') {
        params = { ...params, action_origin: 'export_button_without_filters' };
      } else {
        params = { ...params, action_origin: 'export_button_with_filters' };
      }
    }
    return this.asyncExportManager.exportEntriesTask.perform(params);
  }

  sortEntries(field: string, direction: string): void {
    // set sort
    const dir = direction === 'asc' ? '' : '-';
    this.sort = `${dir}${field}`;
    this.metrics.trackEvent('Entries Sorted', { sort_field_name: field });
  }

  signOutEntries = dropTask(async (entries: Entry[]) => {
    this.workplaceMetrics.trackEvent('EMPLOYEE_LOG_BULK_SIGN_OUT_CLICKED', {
      entries: entries.map((entry) => entry.id),
    });
    await this.showSignOutConfirmationTask.perform(entries);
    this.clearAll();
    // Refresh metrics after sign-out
    await this.loadEmployeeVisitMetrics.perform();
  });

  signOutEntry = dropTask(async (entry: Entry) => {
    await this.showSignOutConfirmationTask.perform([entry]);
    // Refresh metrics after sign-out
    await this.loadEmployeeVisitMetrics.perform();
  });

  @dropTask
  showSignOutConfirmationTask = <EncapsulatedTask<unknown, [Entry[]], { abort: () => void; continue: () => void }>>(<
    unknown
  >{
    *perform(
      this: { entries: Entry[]; abort: () => void; continue: () => void },
      entries: Entry[],
    ): Generator<Promise<boolean>, unknown, unknown> {
      this.entries = entries;

      const deferred = defer<boolean>();

      this.abort = () => deferred.reject();
      this.continue = () => deferred.resolve(true);

      return yield deferred.promise;
    },
  });

  // We currently manage the list of entries manually so have to take care of this
  // Not sure if there is a better solution
  removeEntryFromList(entry: Entry): void {
    this.entriesAndInvites = this.entriesAndInvites.filter((e) => e.id !== entry.id);
    A(this.selectedEntries).removeObject(entry);
    this.calculateEntriesCount(-1);
  }

  bulkRemoveEntriesFromList(entries: Entry[]): void {
    const entryIds = entries.map((entry) => entry.id);
    this.entriesAndInvites = this.entriesAndInvites.filter((e) => !entryIds.includes(e.id));
    this.selectedEntries = this.selectedEntries.filter((e) => !entryIds.includes(e.id));
  }

  deleteEntries(entries: Entry[]): void {
    void this.showDeleteConfirmationTask.perform(entries).then((result) => {
      if (result.deleteGroup) {
        this.deleteGroupEntries(entries);
      }

      const entryDeletePromises: Promise<Entry>[] = [];
      entries.forEach((entry) => {
        if (!entry.signInTime) {
          entryDeletePromises.push(entry.destroyRecord());
        }
      });

      void this.bulkDeleteEntriesTask.perform(entryDeletePromises);
      this.bulkRemoveEntriesFromList(entries);
      this.calculateEntriesCount(-entries.length);
      this.clearAll();
    });
  }

  bulkDeleteEntriesTask = dropTask(async (entryPromises: Promise<Entry>[]) => {
    await all(entryPromises);
  });

  deleteGroupEntries(entries: Entry[]): void {
    const parents = entries.filter((entry) => entry.hasAdditionalGuests);
    const parentIds = parents.map((entry) => entry.id);
    // This API is still a WIP - we might end up with something better
    // than this
    this.entriesAndInvites.forEach((entry) => {
      if (!!('signInTime' in entry && entry.signInTime) && parentIds.includes(entry.belongsTo('groupParent').id())) {
        this.removeEntryFromList(entry);
      }
    });
  }

  deleteEntry(entry: Entry): void {
    void this.showDeleteConfirmationTask.perform([entry]).then((result) => {
      if (!entry.signInTime) {
        void this.deleteInvite.perform(entry);
      }
      if (result.deleteGroup) {
        this.deleteGroupEntries([entry]);
      }
      this.removeEntryFromList(entry);
    });
  }

  deleteInvite = task(async (invite: Invite | Entry) => {
    return await invite.destroyRecord();
  });

  @dropTask
  showDeleteConfirmationTask = <
    EncapsulatedTask<{ deleteGroup: boolean }, [Entry[]], { abort: () => void; continue: (args: unknown) => void }>
  >(<unknown>{
    entries: [],

    *perform(
      this: { entries: Entry[]; abort: () => void; continue: (args: unknown) => void },
      entries: Entry[],
    ): Generator<Promise<unknown>, unknown, unknown> {
      this.entries = entries;

      const deferred = defer<unknown>();

      this.abort = () => deferred.reject();
      this.continue = (args: unknown) => deferred.resolve(args);
      return yield deferred.promise;
    },
  });

  @action
  updateInputQuery(email: string | null): void {
    this.workplaceMetrics.trackEvent('EMPLOYEE_LOG_SEARCHED_EMPLOYEES');

    // Set input value immediately.
    // This reduces risk of rendered input reverting to a previous value.
    this.inputQuery = email;
    void this.mutateQuery.perform(this.inputQuery);
  }

  mutateQuery = restartableTask(async (email: string | null) => {
    await timeout(zft(500));

    // "Stop if you've heard this one before"
    // If the current query is the same as the new one, do not proceed!
    // This can happen if the search input modified and reverted rapidly
    // (e.g. with a quick "undo")
    if (this.query === email) return;

    void this.loadEntries.cancelAll();
    this.query = email;
    this.page = 0;
    this.metrics.trackEvent('Entries Searched', { search_value: this.query });
  });

  calculateEntriesCount(extra = 0): void {
    if (this.filter === 'all' || this.filter === 'no-show') {
      this.entriesCount = this.totalLoadedInvites;
    } else {
      this.entriesCount = this.totalLoadedEntries;
    }
    this.entriesCount += extra;
  }

  entryParams(_include = []): EntryParams {
    const include = [
      'platform-jobs',
      'visit-approval',
      'visit-approval.screener-results',
      'visit-approval.screener-reviews',
      ..._include,
    ];
    const offset = (this.page - 1) * this.limit;
    const filter: EntryFilter = {
      location: this.currentLocation.location.id,
      visitor_type: this.selectedFlow,
    };

    if (isPresent(this.query)) {
      filter.query = this.query;
    }

    const featureFlagEnabled = this.abilities.can('see features for dbeam');
    if (featureFlagEnabled) {
      filter['start-date'] = moment(this.startDate).startOf('day').format();
      filter['end-date'] = moment(this.endDate).endOf('day').format();
    } else {
      filter['start-date'] = moment(this.dateWithDefault).startOf('day').format();
      filter['end-date'] = moment(this.dateWithDefault).endOf('day').format();
    }

    if (this.filter === 'signed-in') {
      filter.status = 'not-signed-out';
    }

    if (this.filter === 'signed-out') {
      filter.status = this.filter;
    }

    if (this.selected?.employee) {
      filter.employee = this.selected.employee.id;
    }

    return {
      include: include.join(),
      filter,
      page: { limit: this.limit, offset },
      sort: this.sort,
    };
  }

  loadBosses = dropTask(async () => {
    try {
      const employee = this.currentAdmin.employee;
      if (!employee) {
        return;
      }

      try {
        await employee.hasMany('bosses').load();
      } catch (e) {
        this.workplaceMetrics.logMonitorError({
          event: 'EMPLOYEE_LOG_FAILED_TO_LOAD_BOSSES',
          error: e,
        });
      }
      const bossesIds = employee.hasMany('bosses') && employee.hasMany('bosses').ids();

      if (!bossesIds.length) {
        return;
      }
      const employeeBosses = await this.store.query('employee', {
        filter: { id: bossesIds.join(','), deleted: false },
      });
      const bosses = employeeBosses.filter((boss) => boss.id !== employee.id); // prevent self bossing.
      this.bosses = bosses;
    } catch (e) {
      this.logger.error(<Error>e);
    }
  });

  loadEntries = dropTask(async (loadingMore = false) => {
    if (!loadingMore) this.entriesAndInvites = [];

    this.workplaceMetrics.trackEvent('EMPLOYEE_LOG_LOADING_ENTRIES');
    try {
      this.page++;

      const entryParams = this.entryParams();
      const entries = <PaginatedRecordArray<Entry> & { meta: { 'has-entries': boolean } }>(
        await this.store.query('entry', entryParams)
      );
      const invites = <PaginatedRecordArray<Invite>>await this._loadInvitesTask.perform();
      if (entries.length) {
        try {
          await this.store.query('reservation', {
            filter: {
              'entry-id': entries.mapBy('id').join(','),
            },
            include: 'desk',
          });
        } catch (_e) {
          // Silently ignore reservation fetch errors
        }
      }
      await this.pollEventReports.cancelAll();
      void this.pollEventReports.perform();
      // @ts-ignore private function
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.beginPropertyChanges();
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.entriesAndInvites.pushObjects(entries.slice());
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.entriesAndInvites.pushObjects(invites.slice());
      // A side effect of manually managing the entries in our model
      // is that pubnub will occasionally resolve in a way that results
      // in duplicate entires in the model.
      //
      // Here we explicitly dedup the model. A better long term solution
      // will involve a larger refactor.
      this.entriesAndInvites = _uniqBy(this.entriesAndInvites, 'id');
      this.totalLoadedEntries = entries.meta.total;
      this.totalLoadedInvites = invites.meta.total;
      this.hasEntries = entries.meta['has-entries'];
      // @ts-ignore private function
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.endPropertyChanges();
      this.calculateEntriesCount();
    } catch (_e) {
      this.page--;
    }
  });

  inviteParams(additionalInclude: string[] = []): Record<string, unknown> {
    const include = ['platform-jobs', ...additionalInclude];
    const offset = (this.page - 1) * this.limit;
    const limit = this.limit;
    const filter: Record<string, string | null> = {
      location: this.currentLocation.location.id,
      query: this.query,
      date: this.dateWithDefault,
      visitor_type: this.selectedFlow,
    };

    if (isPresent(this.query)) {
      filter['query'] = this.query;
    }

    const featureFlagEnabled = this.abilities.can('see features for dbeam');
    if (featureFlagEnabled) {
      filter['start-date'] = moment(this.startDate).startOf('day').format();
      filter['end-date'] = moment(this.endDate).endOf('day').format();
    } else {
      filter['date'] = this.dateWithDefault;
    }

    // Invite Filtering in envoy-web doesn't support all as a flag
    if (this.filter !== 'all') {
      filter['status'] = this.filter;
    }

    if (this.selected?.employee) {
      filter['employee'] = this.selected.employee.id;
    }

    return {
      filter,
      page: { offset, limit },
      sort: this.invitesSort,
      include: include.join(),
    };
  }

  _loadInvitesTask = enqueueTask(async () => {
    const include = ['visit-approval', 'visit-approval.screener-results', 'visit-approval.screener-reviews'];

    const inviteParams = this.inviteParams(include);
    const invites = await this.store.query('invite', inviteParams);

    if (invites.length) {
      try {
        await this.store.query('reservation', {
          filter: {
            'invite-id': invites.mapBy('id').join(','),
          },
          include: 'desk',
        });
      } catch (_e) {
        // Silently ignore reservation fetch errors
      }
    }
    return invites;
  });

  buildEmployeeQuery(): Record<string, unknown> {
    return {
      page: {
        limit: this.limit,
        offset: this.employeesPage * this.limit,
      },
      filter: {
        'locations.id': this.currentLocation.location.id,
        deleted: false,
      },
      include: 'user,employee-locations',
      sort: 'name',
    };
  }

  loadEmployeesTask = enqueueTask(async () => {
    const employees = <PaginatedRecordArray<Employee>>await this.store.query('employee', this.buildEmployeeQuery());
    this.employeesCount = employees.meta.total;
    if (this.employees.length) {
      // The last option is for loading more
      this.employees.pop();
    }
    this.employees.push(...employees.slice());
    const employeePromises = this.selectedEntries.map((entry) => {
      return this.store.findRecord('employee', entry.belongsTo('employee').id());
    });

    const selectedEmployees = await all(employeePromises);
    this.employees.push(...selectedEmployees);
    this.employees = _uniqBy(this.employees, 'email');
    this.employeesPage++;
    if (this.hasMoreEmployeePages) {
      // this.employees.pushObject('loadMore');
    }
  });

  searchEmployeesTask = restartableTask(async (term: string) => {
    this.workplaceMetrics.trackEvent('EMPLOYEE_LOG_SEARCHED_EMPLOYEES');

    const extraFilters = {
      'locations.id': this.currentLocation.location.id,
    };
    return await this._searchEmployeesTask.perform(term, extraFilters);
  });

  // prettier-ignore
  @(employeesSearcherTask({
    filter: {
      deleted: false,
    },
    include: 'user,employee-locations',
    sort: 'name',
  }).restartable())
  _searchEmployeesTask!: EmployeeSearcherTask;

  get hasMoreEmployeePages(): boolean {
    return this.employeesPage * this.limit < this.employeesCount;
  }

  loadMoreEmployeesTask = dropTask(async () => {
    if (this.hasMoreEmployeePages) {
      try {
        await this.loadEmployeesTask.perform();
      } catch (_e) {
        this.employeesPage--;
      }
    }
  });

  get workplaceDays(): Array<Partial<WorkplaceDay>> {
    let workplaceDays: Array<Partial<WorkplaceDay>> = this.store.peekAll('workplace-day').filter((day) => {
      return day.belongsTo('deskLocation').id() === this.state.currentLocationEnabledDesks?.id;
    });
    if (!workplaceDays.length) {
      workplaceDays = this.defaultWorkplaceDays();
    }
    return workplaceDays;
  }

  defaultWorkplaceDays(): Array<Partial<WorkplaceDay>> {
    return DAYS_OF_WEEK_ARRAY.map((dayOfWeek) => {
      return {
        active: true,
        dayOfWeek: dayOfWeek,
        startTime: '00:00:00',
        endTime: '23:59:59',
      };
    });
  }

  pollEventReports = restartableTask(async () => {
    if (!this.currentLocation.location.hostNotificationsEnabled) {
      return;
    }
    const store = this.store;
    const location = this.currentLocation.location.id;
    let keepPolling = false;
    let maxPolls = NUMBER_OF_POLLING_TRIES;
    do {
      const entryEventReportIdentifiers = store.peekAll('entry').map((entry) => `vr:entry:${entry.id}`);
      const loadedEventReportsIdentifiers = store
        .peekAll('eventReport')
        .filterBy('hasTerminalStatus', true)
        .mapBy('identifier');
      const eventReportIdentifiers = entryEventReportIdentifiers.filter((eventReportIdentifier) => {
        return !loadedEventReportsIdentifiers.includes(eventReportIdentifier);
      });
      const identifierURI = eventReportIdentifiers.join(',');
      if (isBlank(identifierURI) || isBlank(location)) {
        break;
      }
      const newEventReportsQueryResult = await store.query('event-report', {
        filter: { identifier: identifierURI, location },
      });
      const newEventReports = newEventReportsQueryResult.slice();
      if (newEventReports.length) {
        const loadedReports = this.eventReports;
        const newEventIds = newEventReports.map((eventReport) => eventReport.id);
        // Since it's possible that we could have an old event report in "pending" status that has since
        // changed to a "terminal" status, we always take the latest version of any duplicate event reports
        const updatedEventReportList = loadedReports
          .filter((report) => {
            return !newEventIds.includes(report.id);
          })
          .concat(newEventReports);
        this.eventReports = updatedEventReportList;
      }
      // Only poll as long as there are event reports that are either `queued` or `inProgress`.
      const hasPendingEvents = newEventReports.some((event) => event.inProgress || event.queued);
      // The events creation for a new entry is async, so the intial request for a just created entry
      // could be empty, so we give `NUMBER_OF_POLLING_TRIES`
      maxPolls--;
      keepPolling = hasPendingEvents || maxPolls > 1;
      await timeout(EVENT_REPORT_POLLING_TIMEOUT);
    } while ((config.environment !== 'test' || config.testLongPolling) && keepPolling);
  });

  loadEntriesRestartableTask = restartableTask(async () => {
    await timeout(zft(250));
    this.page = 0;
    this.entriesAndInvites = [];
    await this.loadEntries.perform();
    await this.loadEmployeeVisitMetrics.perform();
  });

  loadEmployeeVisitMetrics = restartableTask(async (): Promise<void> => {
    this.workplaceMetrics.trackEvent('EMPLOYEE_LOG_LOADING_METRICS');

    try {
      const featureFlagEnabled = this.abilities.can('see features for dbeam');
      const location = this.currentLocation.location;

      let startDate: Date | string;
      let endDate: Date | string;

      if (featureFlagEnabled && this.startDate && this.endDate) {
        startDate = this.startDate;
        endDate = this.endDate;
      } else {
        startDate = this.dateWithDefault;
        endDate = this.dateWithDefault;
      }

      const options: { visitor_type?: string; employee?: string; scope?: string; status?: string } = {
        visitor_type: this.selectedFlow,
      };

      if (this.selected?.employee) {
        options.employee = this.selected.employee.id;
      }

      if (this.filter === 'mine' || this.filter === 'boss') {
        options.scope = this.filter;
      }

      // Add status filter for signed-in, signed-out, no-show
      if (this.filter === 'signed-in' || this.filter === 'signed-out' || this.filter === 'no-show') {
        options.status = this.filter;
      }

      const metrics = await fetchEmployeeVisitMetrics(this.store, location, startDate, endDate, options);
      this.locationsCapacity = metrics || null;

      this.workplaceMetrics.trackEvent('EMPLOYEE_LOG_METRICS_LOADED');
    } catch (error) {
      this.workplaceMetrics.logMonitorError({
        event: 'EMPLOYEE_LOG_FAILED_TO_LOAD_METRICS',
        error: error,
      });
    }
  });

  /*
   *  Date Range Picker functions
   */

  @action
  onDateRangeSelect(option: string): void {
    this.workplaceMetrics.trackEvent('EMPLOYEE_LOG_DATE_RANGE_SELECT_CLICKED');

    this.selectedDateRange = option;
    let start = startOfDay(new Date());
    let end = endOfDay(new Date());
    switch (option) {
      case 'Yesterday':
        start = subDays(start, 1);
        end = subDays(end, 1);
        break;
      case 'Tomorrow':
        start = addDays(start, 1);
        end = addDays(end, 1);
        break;
      case 'Past 7 days':
        start = subDays(start, 7);
        break;
      case 'Past 14 days':
        start = subDays(start, 14);
        break;
      case 'Past 30 days':
        start = subDays(start, 30);
        break;
      case 'Next 7 days':
        end = addDays(end, 7);
        break;
      case 'Next 14 days':
        end = addDays(end, 14);
        break;
      case 'Next 30 days':
        end = addDays(end, 30);
        break;
      case 'Custom range':
        return;
    }
    this.startDate = addMinutes(start, this.state.minutesBetweenTimezones(start));
    this.endDate = addMinutes(end, this.state.minutesBetweenTimezones(end));
    void this.loadEntriesRestartableTask.perform();
  }

  @dropTask
  reservationModalTask = {
    *perform(this: {
      context: WorkplaceEntriesController;
      abort: () => void;
      continue: () => void;
    }): Generator<Promise<boolean>, unknown, unknown> {
      this.context.workplaceMetrics.trackEvent('EMPLOYEE_LOG_CREATE_RESERVATION_LINK_CLICKED');

      const deferred = defer<boolean>();
      this.abort = () => {
        this.context.workplaceMetrics.trackEvent('EMPLOYEE_LOG_CREATE_RESERVATION_FLOW_ABORTED');

        deferred.resolve(false);
      };
      this.continue = () => {
        this.context.workplaceMetrics.trackEvent('EMPLOYEE_LOG_CREATE_RESERVATION_FLOW_COMPLETED');

        void this.context.loadEntriesRestartableTask.perform();
        deferred.resolve(true);
      };
      return yield deferred.promise;
    },
  };

  clearAll(): void {
    this.selectedEntries = [];
  }

  _loadMore(): Promise<void> {
    // Because of the way loadMore works -- there are scenarios where
    // this can be called if destroyed
    if (this.isDestroyed) {
      return resolve();
    }
    if (this.hasMorePages) {
      return this.loadEntries.perform(true);
    } else {
      return resolve();
    }
  }

  loadMore(): Promise<void> {
    return this._loadMore();
  }

  @action
  toggleDashboardField({ name, componentName, show }: Field): void {
    // componentName is no longer used, but the backend is still validating
    let dashboardFields = this.currentLocation.location.dashboardFields;
    if (show) {
      dashboardFields.push({ name, componentName });
    } else {
      dashboardFields = dashboardFields.filter((field) => field.name !== name);
    }
    this.currentLocation.location.dashboardFields = dashboardFields;
    void this.currentLocation.location.save();
  }

  asyncEntryExportParams(): Record<string, unknown> {
    let params: Record<string, unknown> = {};
    const filter = this.filter;
    const location = this.currentLocation.location;
    let query = this.query;
    const employee = this.selected?.employee;
    if (isBlank(query)) {
      query = null;
      const startDate = this.startDate;
      const endDate = this.endDate;
      if (isPresent(startDate) && isPresent(endDate)) {
        params['endDate'] = moment(endDate).endOf('day').toDate();
        params['startDate'] = moment(startDate).startOf('day').toDate();
      }
    }
    params = Object.assign(params, {
      employee,
      filter,
      location,
      query,
      sort: 'signed-in-at',
    });
    return params;
  }

  _hasActivePluginForCategory(name: string): boolean {
    if (!this.model || !this.model.plugins || !this.model.pluginInstalls) {
      return false;
    }
    const ids = this.model.plugins.filterBy('category', name).mapBy('id');
    return this.model.pluginInstalls.slice().some((install) => {
      const active = install.status !== 'uninstalled';
      return active && ids.includes(install.belongsTo('plugin').id());
    });
  }

  @action
  clearSearch(): void {
    this.page = 0;
    this.selectedFlow = DEFAULT_FLOW_NAME.EMPLOYEE_SCREENING;
    this.inputQuery = null;
    this.query = '';
  }
  handleMessage(message: { event: string }): void {
    if (message.event === 'showEntryExportModal') {
      this.toggleModal();
    } else if (message.event === 'closeExportModal') {
      this.toggleModal();
    }
  }

  @action
  chooseToday(): void {
    this.query = '';
    this.selectedDate = moment();
    this.metrics.trackEvent('Entries Date Changed', {
      date_nav_method: 'go_to_today',
      date_selected: this.selectedDate.toISOString(),
    });
  }

  @action
  closeModal(): void {
    this.startDate = null;
    this.endDate = null;
  }

  @action
  goBack(e: Event): void {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    this.query = '';
    /*
      `moment.subtract` and `moment.add` mutates the value instead of
      returning a copy with the new one, it leads to unexpected
      behaviors specially when we are using CP or observers.
      */
    const date = moment(this.selectedDate);

    this.selectedDate = date.subtract(1, 'day');
    this.metrics.trackEvent('Entries Date Changed', {
      date_nav_method: 'left_arrow',
      date_selected: this.selectedDate.toISOString(),
    });
  }

  @action
  goForward(e: Event): void {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    this.query = '';
    const date = moment(this.selectedDate);

    this.selectedDate = date.add(1, 'day');
    this.metrics.trackEvent('Entries Date Changed', {
      date_nav_method: 'right_arrow',
      date_selected: this.selectedDate.toISOString(),
    });
  }

  @action
  didSelectDate(date: moment.Moment): void {
    this.selectedDate = date;
    this.metrics.trackEvent('Entries Date Changed', {
      date_nav_method: 'calendar_selection',
      date_selected: date.toISOString(),
    });
  }

  @action
  trackGoToToday(): void {
    this.metrics.trackEvent('Entries Date Changed', {
      date_nav_method: 'go_to_today',
      date_selected: moment().toISOString(),
    });
  }

  @action
  toggleCalendarVisible(): void {
    this.mobileCalendarVisible = !this.mobileCalendarVisible;
  }

  @action
  selectFlow(flow: Flow): void {
    // @ts-ignore private function
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.beginPropertyChanges();
    const name = isPresent(flow.id) || flow.name === DEFAULT_FLOW_NAME.EMPLOYEE_SCREENING ? flow.name : '';
    this.selectedFlow = name;
    // @ts-ignore private function
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.endPropertyChanges();
  }

  @action
  selectFilter(option: FilterOption): void {
    // @ts-ignore private function
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.beginPropertyChanges();
    this.filter = option.filter;
    // @ts-ignore private function
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.endPropertyChanges();
    this.metrics.trackEvent('Entries Filtered', { filter_name: option.name });
    // Reload metrics when filter changes
    void this.loadEmployeeVisitMetrics.perform();
  }

  @action
  setDateRange(start: Date, end: Date): void {
    this.startDate = start;
    this.endDate = end;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'workplace.entries': WorkplaceEntriesController;
  }
}
